import React, { useCallback, useMemo, useState } from "react";

import { sortBy } from "lodash";

import {
  Container,
  ScreenContainer,
  ScreenHeader,
  Link as WombatLink,
  H3,
  EmptyState,
  Link,
} from "../components/wombat-ui";
import { MadIcon } from "../components/icons";

import useGetMads from "../graphql/hooks/queries/useGetMads";
import useTranslations from "../hooks/useTranslations";
import useFuse from "../hooks/useFuse";
import useViewer from "../hooks/useViewer";
import FollowUpCard from "../components/mads/FollowUpCard";
import { MadFragment } from "../graphql/fragments";
import List from "../components/utils/List";

export type MadType = "all" | "next" | "ongoing" | "finished" | "available";

const MAD_TABS: { path: string; status: MadType }[] = [
  {
    path: "",
    status: "all",
  },
  {
    path: "/next",
    status: "next",
  },
  {
    path: "/ongoing",
    status: "ongoing",
  },
  {
    path: "/finished",
    status: "finished",
  },
  {
    path: "/available",
    status: "available",
  },
];

type MadListHeaderProps = {
  searchInputValue: string;
  searchResultCount: number;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  madsByStatus: Record<MadType, MadFragment[]>;
  activeFilter: MadType;
};

const MadListHeader: React.FC<MadListHeaderProps> = React.memo(
  ({
    searchInputValue,
    searchResultCount,
    onSearchChange,
    madsByStatus = {},
    activeFilter,
  }) => {
    const [visible, setVisible] = useState(false);

    const t = useTranslations();

    return (
      <div className="pt-4 pb-5 space-y-3 bg-white border-b border-gray-200 sm:flex sm:items-center sm:justify-between sm:space-x-4 sm:space-y-0">
        <div className="flex items-baseline space-x-2 sm:block md:flex">
          <H3>{t(`mads.status.${activeFilter}` as const, { count: 2 })}</H3>
          <span className="text-sm font-medium text-light-900">
            (
            {t("screens.dashboard.searchResultsCount", {
              count:
                searchInputValue === ""
                  ? madsByStatus?.[activeFilter]?.length || 0
                  : searchResultCount,
            })}
            )
          </span>
        </div>
        <div className="sm:min-w-lg">
          <label htmlFor="search_candidate" className="sr-only">
            {t("mads.search") as string}
          </label>
          <div className="flex rounded-md shadow-sm">
            <div className="relative flex-grow focus-within:z-10">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg
                  className="w-5 h-5 text-light-900"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* MIGRATE WOMBAT UI */}
              <input
                id="search_candidate"
                className="block w-full h-full pl-10 transition duration-150 ease-in-out rounded-none rounded-l-md sm:hidden"
                placeholder={t("mads.search") as string}
                value={searchInputValue}
                onChange={onSearchChange}
              />
              <input
                id="search_candidate"
                className="hidden w-full h-full pl-10 transition duration-150 ease-in-out rounded-none rounded-l-md sm:block sm:text-sm sm:leading-5"
                placeholder={t("mads.searchBy") as string}
                value={searchInputValue}
                onChange={onSearchChange}
              />
            </div>
            <button
              onClick={() => setVisible(!visible)}
              className="relative flex items-center px-4 py-2 -ml-px text-sm font-medium leading-5 text-gray-700 transition duration-150 ease-in-out border border-gray-300 rounded-r-md bg-gray-50 hover:text-gray-500 hover:bg-white focus:outline-none focus:shadow-outline-primary focus:border-primary-300 active:bg-gray-100 active:text-gray-700"
            >
              <div className="relative inline-flex">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M5 4a1 1 0 00-2 0v7.268a2 2 0 000 3.464V16a1 1 0 102 0v-1.268a2 2 0 000-3.464V4zM11 4a1 1 0 10-2 0v1.268a2 2 0 000 3.464V16a1 1 0 102 0V8.732a2 2 0 000-3.464V4zM16 3a1 1 0 011 1v7.268a2 2 0 010 3.464V16a1 1 0 11-2 0v-1.268a2 2 0 010-3.464V4a1 1 0 011-1z" />
                </svg>
                <span className="ml-2 truncate">
                  {t(`mads.status.${activeFilter}` as const, { count: 2 })}
                </span>
                <svg
                  className="ml-2.5 -mr-1.5 h-5 w-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* MIGRATE - WOMBAT UI - Menu or popover */}
              <div
                className={`absolute right-0 w-48 mt-2 inset-y-full rounded-md shadow-lg transition ${
                  visible
                    ? "ease-out duration-100 transform opacity-100 scale-100 visible z-10"
                    : "ease-in duration-75 opacity-0 scale-95 invisible"
                }`}
                aria-hidden={!visible}
              >
                <div
                  className="py-1 bg-white rounded-md shadow-xs"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="filter-menu"
                >
                  {MAD_TABS.map(({ path, status }, index) => (
                    <React.Fragment key={`${index}`}>
                      <Link
                        to={`/mads${path}`}
                        onClick={() => setVisible(false)}
                        className="flex px-4 py-2 text-sm text-gray-700 transition duration-150 ease-in-out hover:no-underline hover:bg-gray-100 group"
                        role="menuitem"
                      >
                        <div className="flex items-center justify-center px-1 text-xs text-gray-500 bg-gray-100 rounded-lg group-hover:bg-gray-200 group-hover:text-gray-600">
                          {madsByStatus[status].length > 99
                            ? "99+"
                            : madsByStatus[status].length}
                        </div>
                        <span className="ml-2 truncate">
                          {t(`mads.status.${status}` as const, { count: 2 })}
                        </span>
                      </Link>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }
);

const RESEARCH_OPTIONS = {
  keys: [
    "talentName",
    "referentName",
    "campaignName",
    "reports.skillEvaluations.skill.name",
  ],
  matchIfEmpty: true,
  threshold: 0.2,
  useExtendedSearch: true,
};

// FIXME: Relocate MAdUtilities
export function getMadStatus(mad: MadFragment): MadType {
  if (!mad.talent) return "available";
  else if (new Date(mad.startDate) > new Date()) {
    return "next";
  } else if (
    new Date(mad.startDate) < new Date() &&
    new Date(mad.endDate) > new Date()
  ) {
    return "ongoing";
  } else if (new Date(mad.endDate) < new Date()) {
    return "finished";
  }
}

export function getMadsByStatus(
  mads: MadFragment[]
): Record<MadType, MadFragment[]> {
  const madsByStatus = mads.reduce(
    (madsByStatus, mad) => {
      madsByStatus[getMadStatus(mad)].push(mad);

      return madsByStatus;
    },
    { all: [...mads], next: [], ongoing: [], finished: [], available: [] }
  );

  return madsByStatus;
}

type MadListScreenProps = {
  location: { pathname: string };
};

const MadListScreen: React.FC<MadListScreenProps> = ({
  location: { pathname },
}) => {
  const [searchBarInputValue, setSearchBarInputValue] = useState("");
  const t = useTranslations();
  const { isCapableOfInsertingMads, id: viewerId } = useViewer();

  const handleSearchBarInputValueChange = useCallback(
    (e) => setSearchBarInputValue(e.target.value),
    [setSearchBarInputValue]
  );

  const { data, loading } = useGetMads();
  const mads = useMemo(() => {
    if (data && data?.mads.collection) {
      const sortedDates = sortBy(data?.mads.collection, [
        function (mad) {
          return new Date(mad.createdAt);
        },
      ]);
      return sortedDates;
    }
    return data?.mads?.collection || [];
  }, [data]);

  const madsSearchInput = useMemo(
    () =>
      mads.map((mad) => ({
        ...mad,
        talentName: mad?.talent?.name,
        referentName: mad?.referents?.map(({ name }) => name).join(" "),
        campaignName: mad.campaign.name,
      })),
    [mads]
  );

  const searchResult = useFuse(
    madsSearchInput,
    searchBarInputValue,
    RESEARCH_OPTIONS
  );
  const searchResultIds = useMemo(
    () => searchResult.map(({ item: { id } }) => id),
    [searchResult]
  );

  const madsByStatus = useMemo(() => getMadsByStatus(mads), [mads]);

  const activeMadIndex = useMemo(
    () => MAD_TABS.find(({ path }) => `/mads${path}` === pathname).status,
    [pathname]
  );

  const emptyState = (
    <EmptyState
      title={t("mads.noResultsWithFilters") as string}
      action={
        isCapableOfInsertingMads && (
          <Link to="/mads/new" variant="button">
            {t("screens.dashboard.mads.createMads")}
          </Link>
        )
      }
    />
  );

  const filteredData = useMemo(() => {
    if (searchBarInputValue === "") return madsByStatus[activeMadIndex];
    else if (searchBarInputValue !== "")
      return madsByStatus[activeMadIndex].filter(({ id }) =>
        searchResultIds.includes(id)
      );
  }, [searchBarInputValue, madsByStatus, searchResultIds, activeMadIndex]);

  return (
    <ScreenContainer loading={loading}>
      <ScreenHeader
        title={t("mads.mads") as string}
        description={t("mads.madsDescription") as string}
        icon={MadIcon}
        actions={[
          isCapableOfInsertingMads && (
            <WombatLink variant="button" to="/mads/new" key="new">
              {t("header.createMad")}
            </WombatLink>
          ),
        ]}
      />
      <Container>
        <MadListHeader
          searchInputValue={searchBarInputValue}
          searchResultCount={searchResultIds.length}
          onSearchChange={handleSearchBarInputValueChange}
          madsByStatus={madsByStatus}
          activeFilter={activeMadIndex}
        />
        <List
          id="mad-followups-cards"
          listOnly
          renderAsList={(mad) => (
            <FollowUpCard
              key={mad.id}
              mad={mad}
              madType={getMadStatus(mad)}
              isTalentViewing={viewerId === mad.talent.id}
            />
          )}
          data={filteredData}
          renderEmptyState={() => emptyState}
        />
      </Container>
    </ScreenContainer>
  );
};

export default MadListScreen;
